import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  DonationSchema,
  DonationCreateInputSchema,
  DonationUpdateInputSchema,
  type Donation,
  type DonationCreateInput,
  type DonationUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `donation` (contribution volontaire, org-wide en v0).
 *
 * Calque le patron `asset` : l'entrée est VALIDÉE par Zod
 * (`DonationCreateInputSchema`) AVANT toute écriture Firestore. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 *
 * Sécurité : org-wide en v0 (même doctrine qu'asset). Les champs financiers
 * sensibles (montant, reçu fiscal) restent dans le document ; leur protection
 * (accès réservé aux rôles trésorerie / direction, RGPD art. 9 sur l'historique
 * de dons) passe par le contrôle d'accès par rôle à venir, comme payment /
 * expense / order (cf. @taki/data-model).
 */

export type { DonationCreateInput };

/** Nom de la collection Firestore de la famille donation. */
export const DONATIONS_COLLECTION = 'donations';

/**
 * Repository typé pour la famille `donation`.
 * Toute lecture est validée par `DonationSchema`, toute écriture par
 * `DonationCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface DonationRepository {
  create(input: DonationCreateInput): Promise<Donation>;
  list(options?: ListOptions): Promise<Donation[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Donation | null>;
  update(id: string, patch: DonationUpdateInput): Promise<Donation>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Donation>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Donation>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Donation>;
}

const donationConverter = createConverter<Donation>((raw: DocumentData) => DonationSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createDonationRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): DonationRepository {
  const col = collection(db, DONATIONS_COLLECTION).withConverter(donationConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Donation>(db, col, DONATIONS_COLLECTION, getActorId);

  return {
    async create(input: DonationCreateInput): Promise<Donation> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = DonationCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.donorContactId !== undefined) optional['donorContactId'] = fields.donorContactId;
      if (fields.amount !== undefined) optional['amount'] = fields.amount;
      if (fields.inKindDescription !== undefined) optional['inKindDescription'] = fields.inKindDescription;
      if (fields.receivedAt !== undefined) optional['receivedAt'] = fields.receivedAt;
      if (fields.receiptNumber !== undefined) optional['receiptNumber'] = fields.receiptNumber;
      if (fields.paymentId !== undefined) optional['paymentId'] = fields.paymentId;
      if (fields.recurringSubscriptionId !== undefined) optional['recurringSubscriptionId'] = fields.recurringSubscriptionId;
      if (fields.campaignId !== undefined) optional['campaignId'] = fields.campaignId;
      if (fields.projectId !== undefined) optional['projectId'] = fields.projectId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Donation);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return DonationSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async list(options?: ListOptions): Promise<Donation[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Donation | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    async update(id: string, patch: DonationUpdateInput): Promise<Donation> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = DonationUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, DONATIONS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`donation ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
